import { Minus, Plus, Sparkles, TrendingUp } from 'lucide-react';
import {
  formatGBP,
  formatGBPfromPence,
  calculateOrderTotalPence,
  EVEN_TICKET_PRICE_PENCE,
} from '@/lib/format';
import { useHaptic } from '@/hooks/useHaptic';

/**
 * Persistent bottom-of-viewport checkout bar for competition pages.
 *
 * Design principles (see product brief “Persistent checkout bar”):
 *   • Uses THE EXACT SAME odd/even pricing helper as the inline TicketSelector
 *     (no re-implementation) → numbers cannot diverge.
 *   • Reads/writes the same `quantity` state passed down from the parent
 *     (CompetitionPage) → single source of truth, both surfaces always agree.
 *   • Sits above the iOS home-indicator via `env(safe-area-inset-bottom)`.
 *   • Condenses the existing savings/nudge messaging (pair banner, even
 *     savings, odd “+1” nudge) to fit a slim horizontal bar without changing
 *     its meaning.
 *   • Hidden on the very small mobile keyboard-open case by relying on CSS
 *     safe-area — no JS keyboard detection needed for v1.
 */
export const StickyCheckoutBar = ({
  price,
  quantity,
  setQuantity,
  maxQty = 100,
  onCheckout,
  disabled = false,
  ctaLabel = 'Checkout',
  hidden = false,
}) => {
  const haptic = useHaptic();
  const set = (v) => {
    const next = Math.max(1, Math.min(maxQty, v));
    if (next !== quantity) haptic('light');
    setQuantity(next);
  };
  const totalPence = calculateOrderTotalPence(price, quantity);
  const perTicketPence = quantity > 0 ? Math.round(totalPence / quantity) : 0;
  const flatBasePence = Math.round((price || 0) * 100) * quantity;
  const savingsPence = flatBasePence - totalPence;
  const isEven = quantity > 0 && quantity % 2 === 0;
  const isOddNudge = quantity > 1 && quantity % 2 === 1;
  const evenPriceGbp = EVEN_TICKET_PRICE_PENCE / 100;

  if (hidden) return null;

  return (
    <div
      className="fixed left-0 right-0 bottom-0 z-40 border-t-2 border-[color:var(--pd-accent-yellow)]/40 bg-surface/95 backdrop-blur-md shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.5)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="sticky-checkout-bar"
      role="region"
      aria-label="Checkout"
    >
      {/* Slim contextual nudge line — mirrors the inline callouts but 1-line. */}
      {(isEven && savingsPence > 0) || isOddNudge ? (
        <div className="px-4 pt-2 pb-1 text-[11px] leading-tight flex items-center justify-center gap-1.5 text-[color:var(--pd-gold)]">
          {isEven ? (
            <>
              <TrendingUp size={12} />
              <span data-testid="sticky-bar-savings">
                Best value — you’re saving <span className="font-mono">{formatGBPfromPence(savingsPence)}</span> ({formatGBPfromPence(perTicketPence)}/ticket)
              </span>
            </>
          ) : (
            <>
              <Sparkles size={12} />
              <span data-testid="sticky-bar-nudge">
                Add 1 more for just <span className="font-mono">{formatGBP(evenPriceGbp)}</span> —
              </span>
              <button
                type="button"
                onClick={() => set(quantity + 1)}
                className="underline font-mono hover:no-underline"
                data-testid="sticky-bar-plus-one"
              >
                add now
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="px-4 pt-2 pb-1 text-[11px] leading-tight text-center text-muted-sage">
          <span className="font-mono">1st {formatGBP(price)}</span>
          <span className="mx-1.5">·</span>
          <span className="font-mono">every 2nd only {formatGBP(evenPriceGbp)}</span>
        </div>
      )}

      <div className="px-4 pt-1 pb-3 flex items-center gap-3">
        {/* Stepper */}
        <div className="flex items-center gap-1.5 bg-[color:var(--pd-bg)] border border-pd rounded-xl p-1" data-testid="sticky-bar-stepper">
          <button
            type="button"
            onClick={() => set(quantity - 1)}
            className="h-9 w-9 rounded-lg hover:bg-[color:var(--pd-accent-yellow)]/10 flex items-center justify-center disabled:opacity-40"
            aria-label="Decrease quantity"
            data-testid="sticky-bar-decrease"
            disabled={quantity <= 1}
          >
            <Minus size={16} />
          </button>
          <div
            className="min-w-[2ch] text-center font-mono text-base text-[color:var(--pd-text)] tabular-nums"
            data-testid="sticky-bar-qty"
            aria-live="polite"
          >
            {quantity}
          </div>
          <button
            type="button"
            onClick={() => set(quantity + 1)}
            className="h-9 w-9 rounded-lg hover:bg-[color:var(--pd-accent-yellow)]/10 flex items-center justify-center disabled:opacity-40"
            aria-label="Increase quantity"
            data-testid="sticky-bar-increase"
            disabled={quantity >= maxQty}
          >
            <Plus size={16} />
          </button>
        </div>

        {/* Total */}
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-widest text-muted-sage leading-none">Total</div>
          <div className="font-display text-2xl text-[color:var(--pd-accent-yellow)] leading-tight" data-testid="sticky-bar-total">
            {formatGBPfromPence(totalPence)}
          </div>
        </div>

        {/* CTA */}
        <button
          type="button"
          onClick={onCheckout}
          disabled={disabled}
          className="h-12 px-5 rounded-xl bg-[color:var(--pd-accent-yellow)] text-[#14251c] font-medium text-sm shadow-lg shadow-black/20 hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          data-testid="sticky-bar-checkout"
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
};

export default StickyCheckoutBar;
